/**
 * `/integrations` — public format-hub page (moat plan §4.3).
 * Same `.mk` vocabulary as the landing: light-only, no shadcn primitives inside `.mk`.
 */
import { createFileRoute } from '@tanstack/react-router'
import { Check, Minus } from 'lucide-react'
import { Container, CtaPair, FeatureCol, MkFooter, MkNav, SectionHead } from '#/components/landing/mk'
import { EXPORT_FORMATS } from '#/lib/export/common'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/integrations')({
  head: () => ({
    meta: [
      { title: m.hub_meta_title() },
      { name: 'description', content: m.hub_meta_desc() },
      { property: 'og:title', content: m.hub_meta_title() },
      { property: 'og:description', content: m.hub_meta_desc() },
      { property: 'og:type', content: 'website' },
    ],
  }),
  component: Integrations,
})

type Group = 'specs' | 'trackers' | 'agents' | 'whiteboards'
interface Row {
  name: string
  group: Group
  /** `?format=` id, when the plan can leave as this tool's native files */
  exportId?: string
  /** `--from` id, when Bystep can read the tool's files back */
  importId?: string
  /** `bystep init` writes the task loop into this agent's config (packages/cli/src/skill.js TARGETS) */
  skill?: boolean
  /** the agent can drive Bystep through the MCP server */
  mcp?: boolean
  note: string
}

const ROWS: Row[] = [
  { name: 'Kiro', group: 'specs', exportId: 'kiro', mcp: true, note: '.kiro/specs/<slug>/{requirements,design,tasks}.md' },
  { name: 'Spec Kit', group: 'specs', exportId: 'speckit', importId: 'speckit', note: 'specs/<n>-<name>/{spec,plan,tasks}.md' },
  { name: 'OpenSpec', group: 'specs', exportId: 'openspec', importId: 'openspec', note: 'openspec/{project.md,specs/…,changes/…}' },
  { name: 'Backlog.md', group: 'trackers', exportId: 'backlog', importId: 'backlog', note: 'backlog/tasks/task-<n> - <title>.md' },
  { name: 'Task Master', group: 'trackers', exportId: 'taskmaster', importId: 'taskmaster', note: '.taskmaster/tasks/tasks.json + docs/prd.txt' },
  { name: 'Claude Code Tasks', group: 'trackers', exportId: 'claude-tasks', skill: true, mcp: true, note: '.claude/tasks/<plan>.json' },
  { name: 'GitHub Issues', group: 'trackers', exportId: 'github-issues', note: 'issues.json + milestones.json + import.sh' },
  { name: 'Linear', group: 'trackers', exportId: 'linear', note: 'linear.csv + linear.json' },
  { name: 'Cursor', group: 'agents', skill: true, mcp: true, note: '.cursor/rules/bystep.mdc' },
  { name: 'Codex', group: 'agents', skill: true, mcp: true, note: 'AGENTS.md' },
  { name: 'GitHub Copilot', group: 'agents', skill: true, mcp: true, note: '.github/copilot-instructions.md' },
  { name: 'OpenCode', group: 'agents', skill: true, mcp: true, note: '.opencode/skills/bystep/SKILL.md' },
  { name: 'Gemini CLI', group: 'agents', skill: true, mcp: true, note: '.gemini/GEMINI.md' },
  { name: 'Miro', group: 'whiteboards', importId: 'miro', note: 'REST v2 board items → Design Room' },
  { name: 'FigJam', group: 'whiteboards', importId: 'figjam', note: 'copy sticky sebagai teks/CSV → Design Room' },
  { name: 'Excalidraw', group: 'whiteboards', importId: 'excalidraw', note: '.excalidraw JSON → Design Room' },
  { name: 'Markdown / Notion', group: 'whiteboards', importId: 'markdown', note: 'heading + bullet → Design Room' },
]

const GROUP_LABEL: Record<Group, () => string> = {
  specs: () => m.hub_group_specs(),
  trackers: () => m.hub_group_trackers(),
  agents: () => m.hub_group_agents(),
  whiteboards: () => m.hub_group_whiteboards(),
}

function Mark({ on }: { on: boolean }) {
  return on ? (
    <span className="inline-flex items-center justify-center">
      <Check className="size-[18px] text-[var(--mk-accent)]" aria-hidden />
      <span className="sr-only">{m.hub_yes()}</span>
    </span>
  ) : (
    <span className="inline-flex items-center justify-center">
      <Minus className="size-[18px] text-[var(--mk-fg-faint)]" aria-hidden />
      <span className="sr-only">{m.hub_no()}</span>
    </span>
  )
}

function Hero() {
  return (
    <section className="pt-16 pb-10 lg:pt-24 lg:pb-14">
      <Container>
        <SectionHead
          overline={m.hub_hero_overline()}
          title={m.hub_hero_title()}
          lead={m.hub_hero_lead()}
          actions={<CtaPair primary={m.hub_hero_cta()} secondary={m.hub_hero_cta2()} secondaryTo="/docs" />}
        />
      </Container>
    </section>
  )
}

function Matrix() {
  const groups: Group[] = ['specs', 'trackers', 'agents', 'whiteboards']
  return (
    <section className="py-14 lg:py-20">
      <Container>
        <SectionHead align="left" overline={m.hub_matrix_overline()} title={m.hub_matrix_title()} lead={m.hub_matrix_lead()} />
        <div className="mk-card-white mt-12 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-[15px]">
            <thead>
              <tr className="border-b border-[var(--mk-border)] text-sm text-[var(--mk-fg-muted)]">
                <th scope="col" className="px-6 py-4 font-medium">
                  {m.hub_col_tool()}
                </th>
                <th scope="col" className="px-4 py-4 text-center font-medium">
                  {m.hub_col_export()}
                </th>
                <th scope="col" className="px-4 py-4 text-center font-medium">
                  {m.hub_col_import()}
                </th>
                <th scope="col" className="px-4 py-4 text-center font-medium">
                  {m.hub_col_skill()}
                </th>
                <th scope="col" className="px-4 py-4 text-center font-medium">
                  {m.hub_col_mcp()}
                </th>
              </tr>
            </thead>
            {groups.map((g) => (
              <tbody key={g}>
                <tr>
                  <th scope="colgroup" colSpan={5} className="bg-[var(--mk-bg-muted)] px-6 py-2.5 text-left text-xs font-semibold tracking-[0.08em] text-[var(--mk-fg-muted)] uppercase">
                    {GROUP_LABEL[g]()}
                  </th>
                </tr>
                {ROWS.filter((r) => r.group === g).map((r) => (
                  <tr key={r.name} className="border-b border-[var(--mk-border)] last:border-b-0">
                    <th scope="row" className="px-6 py-4 font-normal">
                      <span className="font-medium text-[var(--mk-fg)]">{r.name}</span>
                      <span className="mt-0.5 block font-mono text-[12px] break-all text-[var(--mk-fg-faint)]">{r.note}</span>
                    </th>
                    <td className="px-4 py-4 text-center">
                      <Mark on={Boolean(r.exportId)} />
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Mark on={Boolean(r.importId)} />
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Mark on={Boolean(r.skill)} />
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Mark on={Boolean(r.mcp)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
        <div className="grid gap-12 pt-14 md:grid-cols-2 lg:grid-cols-4">
          <FeatureCol title={m.hub_note_export_title()}>{m.hub_note_export()}</FeatureCol>
          <FeatureCol title={m.hub_note_import_title()}>{m.hub_note_import()}</FeatureCol>
          <FeatureCol title={m.hub_note_agent_title()}>{m.hub_note_agent()}</FeatureCol>
          <FeatureCol title={m.hub_note_room_title()}>{m.hub_note_room()}</FeatureCol>
        </div>
      </Container>
    </section>
  )
}

function Snippet({ label, lines }: { label: string; lines: string[] }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm text-[var(--mk-dark-fg-muted)]">{label}</div>
      <pre className="overflow-x-auto rounded-[12px] border border-[var(--mk-dark-border)] bg-[var(--mk-dark-card)] px-5 py-4 font-mono text-[13px] leading-relaxed text-[var(--mk-dark-fg)]">
        <code>{lines.join('\n')}</code>
      </pre>
    </div>
  )
}

function CliBlock() {
  const formats = EXPORT_FORMATS.map((f) => f.id).join(' · ')
  return (
    <section className="mk-dark-section py-20 lg:py-28">
      <Container>
        <SectionHead align="left" overline={m.hub_cli_overline()} title={m.hub_cli_title()} lead={m.hub_cli_lead()} />
        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          <Snippet
            label={m.hub_cli_import_label()}
            lines={['npx @bystep/cli import --from speckit ./specs', 'npx @bystep/cli import --from openspec ./openspec', 'npx @bystep/cli import --from backlog ./backlog', 'npx @bystep/cli import --from taskmaster ./.taskmaster']}
          />
          <Snippet
            label={m.hub_cli_export_label()}
            lines={['curl -H "authorization: Bearer $BYSTEP_TOKEN" \\', '  "https://bystep.dev/api/plan/$PLAN_ID/export?format=openspec" \\', '  -o plan-openspec.zip']}
          />
        </div>
        <p className="mt-8 text-sm text-[var(--mk-dark-fg-muted)]">
          {m.hub_cli_formats_label()}: <span className="font-mono">{formats}</span>
        </p>
        <p className="mt-2 text-sm text-[var(--mk-dark-fg-muted)]">{m.hub_cli_caption()}</p>
      </Container>
    </section>
  )
}

/** non-tech F4: Bystep’s own hosted MCP server — the one connector you add in the chat app, not in a repo. */
function HostedMcp() {
  return (
    <section className="py-14 lg:py-20">
      <Container>
        <SectionHead align="left" overline={m.mcphosted_hub_overline()} title={m.mcphosted_hub_title()} lead={m.mcphosted_hub_lead()} />
        <div className="mk-card-white mt-12 grid gap-8 p-8 md:grid-cols-2">
          <div className="flex flex-col gap-3">
            <div className="text-sm text-[var(--mk-fg-muted)]">{m.mcphosted_hub_url_label()}</div>
            <code className="rounded-[10px] border border-[var(--mk-border)] bg-[var(--mk-bg-muted)] px-4 py-3 font-mono text-[13px] break-all">https://bystep.dev/api/mcp</code>
            <p className="mk-muted text-sm leading-relaxed">{m.mcphosted_hub_auth()}</p>
            <div className="mt-1 text-sm text-[var(--mk-fg-muted)]">{m.mcphosted_hub_tools_label()}</div>
            <code className="font-mono text-[12px] break-all text-[var(--mk-fg-faint)]">list_plans · plan_status · run_next · run_status · preview_url · approve_stage · escalate</code>
          </div>
          <ul className="flex flex-col gap-3 text-[15px]">
            <li>{m.mcphosted_hub_claude()}</li>
            <li>{m.mcphosted_hub_chatgpt()}</li>
            <li>{m.mcphosted_hub_cursor()}</li>
            <li>
              <a className="underline underline-offset-4" href="/docs#mcp-hosted">
                {m.mcphosted_hub_docs()}
              </a>
            </li>
          </ul>
        </div>
      </Container>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="py-24 lg:py-32">
      <Container>
        <SectionHead title={m.hub_cta_title()} lead={m.hub_cta_lead()} actions={<CtaPair primary={m.hub_cta_primary()} secondary={m.hub_cta_secondary()} secondaryTo="/consulting" />} />
      </Container>
    </section>
  )
}

function Integrations() {
  return (
    <div className="mk h-full overflow-x-hidden overflow-y-auto scroll-smooth">
      <MkNav />
      <main>
        <Hero />
        <Matrix />
        <HostedMcp />
        <CliBlock />
        <FinalCta />
      </main>
      <MkFooter />
    </div>
  )
}
